import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getTenantScope, endpoints } from "@/lib/api";
import RequestAssistanceModal from "./RequestAssistanceModal";

/** Floating, always-visible "Request ISOQuest Assistance" button.
 * Shows on any page where a client context is available (client role OR /clients/:slug route OR ?client=<slug>). */
export default function RequestAssistanceFab() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(getTenantScope());
  const [routeCtx, setRouteCtx] = useState({ slug: null, name: null });
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const onChange = () => setScope(getTenantScope());
    window.addEventListener("isoquest:scope-changed", onChange);
    return () => window.removeEventListener("isoquest:scope-changed", onChange);
  }, []);

  // Resolve client context from route or query
  useEffect(() => {
    let slug = null;
    // /clients/:slug
    const m = location.pathname.match(/^\/clients\/([^/]+)/);
    if (m) slug = m[1];
    // ?client=<slug>
    if (!slug) {
      const q = new URLSearchParams(location.search).get("client");
      if (q && q !== "all") slug = q;
    }
    // client role scope
    if (!slug && scope?.role === "client") slug = scope.slug;
    if (!slug) { setRouteCtx({ slug: null, name: null }); return; }
    endpoints.getClient(slug).then((c) => setRouteCtx({ slug, name: c.company_name })).catch(() => setRouteCtx({ slug, name: slug }));
  }, [location.pathname, location.search, scope]);

  if (!routeCtx.slug) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2" data-testid="assistance-fab-wrap">
        <div className="hidden sm:block bg-white/95 dark:bg-slate-900/90 backdrop-blur border border-slate-200 dark:border-white/10 shadow-lg rounded-xl px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200">
          Need help? <span className="font-semibold">Ask ISOQuest</span>
        </div>
        <Button
          data-testid="assistance-fab-button"
          onClick={() => setOpen(true)}
          className="brand-gradient text-white shadow-xl shadow-indigo-500/40 rounded-full h-14 px-5 gap-2 hover:scale-[1.03] transition-transform"
          size="lg"
        >
          <LifeBuoy className="h-5 w-5" />
          Request ISOQuest Assistance
        </Button>
      </div>
      <RequestAssistanceModal
        open={open}
        onOpenChange={setOpen}
        clientSlug={routeCtx.slug}
        clientName={routeCtx.name}
      />
    </>
  );
}
